import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Typography } from '@mui/material';
import { TranslationKeys } from 'app/translations';
import type { InputFactoryOnChangeFunc } from '../FormikField';
import { InputFieldFactory } from '../FormikField';
import { LinearProgressStyled } from './NotesInput.styles';
import { debounce } from '../../utils';

type NotesInputProps = {
  inputValue: string;
  onSaveNote: (inputValue: string) => void;
  onTextChange?: (text: string) => void;
  isLoading?: boolean;
  isNotAllowed?: boolean;
  debounceTime?: number;
  saveOnBlur?: boolean;
};

export const NotesInput = ({
  inputValue,
  onSaveNote,
  onTextChange,
  saveOnBlur = false,
  isLoading = false,
  isNotAllowed = false,
  debounceTime = 2000,
}: NotesInputProps) => {
  const [localValue, setLocalValue] = React.useState(inputValue);

  React.useEffect(() => {
    if (!onTextChange) {
      setLocalValue(inputValue);
    }
  }, [inputValue, onTextChange]);

  const debouncedSave = React.useMemo(() => debounce(onSaveNote, debounceTime), [debounceTime, onSaveNote]);

  const handleChange = React.useCallback<InputFactoryOnChangeFunc>(
    ({ value }) => {
      if (onTextChange) {
        onTextChange(value);
      } else {
        setLocalValue(value);
      }
      debouncedSave(value);
    },
    [onTextChange, debouncedSave],
  );

  const handleBlur = React.useCallback(() => {
    if (saveOnBlur) {
      debouncedSave.cancel();
      const currentValue = onTextChange ? inputValue : localValue;
      onSaveNote(currentValue);
    }
  }, [saveOnBlur, onTextChange, inputValue, localValue, onSaveNote, debouncedSave]);

  React.useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  const currentValue = onTextChange ? inputValue : localValue;

  const content = (
    <>
      <Typography variant="h6">
        <FormattedMessage id={TranslationKeys.notes} />
      </Typography>

      <InputFieldFactory
        onChange={handleChange}
        field={{
          name: 'notes',
          type: 'text',
          multiline: true,
          minRows: 5,
          value: currentValue,
          loading: isLoading,
          disabled: isNotAllowed,
        }}
      />

      {isLoading && <LinearProgressStyled color={'secondary'} />}
    </>
  );

  if (saveOnBlur) {
    return (
      <div onBlur={handleBlur} style={{ width: '100%' }}>
        {content}
      </div>
    );
  }

  return content;
};
